import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { OperatorMessageName, OperatorMessenger } from './operator-messenger';
import { environment } from 'src/environments/environment';
import { Theme, ThemeService } from '../theme/theme.service';
import { DepositService } from '../deposit/deposit.service';
import { TranslationService } from '../translation/translation.service';
import { StorageService } from '../storage/storage.service';
import { GameId } from 'src/app/model/game/game-id';

export const OperatorPlatform = {
  ios: 'ios',
  android: 'android',
  desktopweb: 'desktopweb',
  mobileweb: 'mobileweb',
  telegram: 'telegram',
} as const;

export const OperatorId = {
  thndr: 'thndr',
  betopenly: 'betopenly',
  maincard: 'maincard',
} as const;

interface Operator {
  id?: string;
  gameId?: string;
  language?: string;
  lightningAddress?: string;
  platform?: string;
}

interface OperatorConfig {
  operatorId?: string;
  gameId?: string;
  showClose?: boolean;
  lightningAddress?: string;
  language?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OperatorService {
  private themeService = inject(ThemeService);
  private currencyPipe = inject(CurrencyPipe);
  private depositService = inject(DepositService);
  private translationService = inject(TranslationService);
  private storageService = inject(StorageService);

  private canceledInvoiceSignal = signal<string | undefined>(undefined); // New signal for canceled invoices
  private balanceSignal = signal<number | undefined>(undefined);
  private currencySignal = signal<string | undefined>(undefined);
  private messenger: OperatorMessenger | undefined;
  private configSignal = signal<OperatorConfig | undefined>(undefined);
  private sdkVersionSignal = signal<string | undefined>(undefined);
  private operator?: Operator;

  async initialise(
    tokenCallback: (token: string) => Promise<void>,
    operator?: Operator,
  ): Promise<void> {
    this.messenger = new OperatorMessenger(
      this.sdkVersionCallback.bind(this),
      tokenCallback,
      this.configCallback.bind(this),
      this.balanceCallback.bind(this),
      this.cancelInvoiceCallback.bind(this),
    );

    OperatorMessenger.sendMessage(OperatorMessageName.GET_SDK_VERSION);

    if (!operator) {
      OperatorMessenger.sendMessage(OperatorMessageName.GET_CONFIG);
    } else {
      this.operator = operator;

      this.setLanguage(operator?.language || 'en');
      this.setTheme(operator?.id);

      OperatorMessenger.sendMessage(OperatorMessageName.GET_TOKEN);
    }
  }

  handlePaymentError(error: unknown) {
    return this.messenger?.sendPaymentError(error);
  }

  redirectToOperator() {
    // TODO: replace with the OperatorMessageName.CLOSE message.
    OperatorMessenger.sendMessage(OperatorMessageName.REDIRECT);
  }

  async updateBalance(): Promise<void> {
    OperatorMessenger.sendMessage(OperatorMessageName.GET_BALANCE);
  }

  payLightningInvoice(invoice: string): void {
    OperatorMessenger.postMessage(
      {
        message: OperatorMessageName.PAY_INVOICE,
        data: {
          invoice: invoice,
        },
      },
      '*',
    );
  }

  updateDemoBalance(balanceInc: number): void {
    OperatorMessenger.sendMessage(OperatorMessageName.DEMO_BALANCE_UPDATE, { balanceInc });
  }

  static get IsDemo(): boolean {
    return environment.deployment === 'demo';
  }

  get sdkVersion() {
    return this.sdkVersionSignal.asReadonly();
  }

  get operatorId() {
    if (this.operator?.id) {
      return this.operator?.id;
    }
    throw new Error('Operator ID is not set');
  }

  get balance() {
    return this.balanceSignal;
  }

  get isLightning() {
    return this.lightningAddress !== undefined;
  }

  get lightningAddress() {
    return this.operator?.lightningAddress || this.config()?.lightningAddress;
  }

  get canceledInvoice() {
    return this.canceledInvoiceSignal;
  }

  get gameId() {
    return this.operator?.gameId || GameId.Solitaire;
  }

  get operatorPlatform() {
    return this.operator?.platform;
  }

  get formattedBalance() {
    return computed(() => {
      const balance = this.balanceSignal();

      if (balance === undefined) {
        return undefined;
      }

      // TODO: When BetOpenly fixes their bug, remove this IF
      if (this.operatorId === OperatorId.betopenly) {
        return this.currencyPipe.transform(balance, 'USD', 'symbol', '1.2-2');
      }

      if (this.currency() === 'btc') {
        return `${balance} BTC`;
      } else if (this.currency() === 'cents') {
        return this.currencyPipe.transform(balance / 100, 'USD', 'symbol', '1.2-2');
      } else if (this.currency() === 'sats') {
        return `${this.themeService.formatNumber(balance)} sats`;
      } else if (this.currency() === 'mcn') {
        return this.themeService.formatNumber(balance);
      } else {
        return undefined;
      }
    });
  }

  private get config(): Signal<OperatorConfig | undefined> {
    return this.configSignal.asReadonly();
  }

  private get currency() {
    return this.currencySignal;
  }

  private sdkVersionCallback(sdkVersion: string) {
    this.sdkVersionSignal.set(sdkVersion);
  }

  // deprecated - remove when all operators are migrated to version 2.0.0+ of the THNDR SDK.
  private configCallback(config: OperatorConfig) {
    this.operator = {
      id: config.operatorId,
      gameId: config.gameId,
      language: config.language,
    };

    this.configSignal.set({ ...config });
    OperatorMessenger.sendMessage(OperatorMessageName.GET_TOKEN);
    this.setLanguage(config.language || 'en');
    this.setTheme(config.operatorId);
  }

  private setTheme(operatorId?: string) {
    // TODO: Make this dependent on the operatorId.
    if (operatorId === OperatorId.betopenly) {
      this.themeService.switch(Theme.BetOpenly);
    } else if (operatorId === OperatorId.maincard) {
      this.themeService.switch(Theme.Maincard);
    } else if (operatorId === OperatorId.thndr) {
      this.themeService.switch(Theme.THNDR);
    } else if (operatorId === 'demo') {
      this.storageService
        .getItem('demo-theme')
        .then((theme) => {
          this.themeService.switch(theme as Theme);
        })
        .catch(() => {
          this.themeService.switch(Theme.Crypto);
        });
    } else {
      // Speed and other operators who don't white label
      this.themeService.switch(Theme.Clinch);
    }
  }

  private setLanguage(language: string) {
    const name = this.translationService.getLanguageNameFromIso(language) || 'en';
    this.translationService.switchLanguage(name);
  }

  private balanceCallback(balance: number, currency: string) {
    this.balanceSignal.set(balance);
    this.currencySignal.set(currency);
  }

  private cancelInvoiceCallback(invoice: string) {
    this.depositService.removeInvoice(invoice);
    this.canceledInvoiceSignal.set(invoice); // Update the signal when the invoice is canceled
  }

  refreshTokenIfNeeded(): void {
    OperatorMessenger.sendMessage(OperatorMessageName.GET_TOKEN);
  }
}
